// src/pages/Exams/generate/CoverageReport.tsx
// Design `.disc` "Coverage report · questions per chapter vs target", open by
// default: one row per chapter with a bar, "got / want", a status pill and the
// per-chapter retry action. Rows page at 10.
import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge, PagedFooter, Segmented, Term, Track } from '../../../school_panel/components/ui';
import { usePagination } from '../../../school_panel/lib/usePagination';
import type { Blueprint, FailedChapter, QuestionDraft } from './types';

interface Props {
  blueprint: Blueprint;
  questions: QuestionDraft[];
  chapterNames?: Record<number, string>;
  failedChapters?: FailedChapter[];
  formLabels?: string[];
  onFilterChapter?: (chapterId: number) => void;
  /** Total the user asked for; shown next to the planned total when they differ. */
  requestedTotal?: number;
  onRetryChapter?: (formLabel: string, chapterId: number) => Promise<unknown>;
}

const MINI = 'inline-flex min-h-[34px] items-center rounded-[10px] border border-line-strong bg-surface px-[11px] text-xs font-semibold text-ink hover:border-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

export function CoverageReport({
  blueprint, questions, chapterNames, failedChapters, formLabels,
  onFilterChapter, onRetryChapter, requestedTotal,
}: Props) {
  const [retryForm, setRetryForm] = useState(() => formLabels?.[0] ?? 'A');
  const [open, setOpen] = useState(true);

  const deliveredByChapter = useMemo(() => {
    const m = new Map<number, number>();
    questions.forEach(q => m.set(q.chapter_id, (m.get(q.chapter_id) ?? 0) + 1));
    return m;
  }, [questions]);

  const lowByChapter = useMemo(() => {
    const m = new Map<number, number>();
    questions.forEach(q => { if ((q.quality_score ?? 100) < 50) m.set(q.chapter_id, (m.get(q.chapter_id) ?? 0) + 1); });
    return m;
  }, [questions]);

  const noSourceIds = useMemo(
    () => new Set((failedChapters ?? []).filter(f => f.reason === 'no_source').map(f => f.chapter_id)),
    [failedChapters]);

  const rowsAll = useMemo(
    () => blueprint.chapter_distribution
      .filter(e => e.count > 0 || (deliveredByChapter.get(e.chapter_id) ?? 0) > 0)
      .map(e => ({ ...e, got: deliveredByChapter.get(e.chapter_id) ?? 0 })),
    [blueprint.chapter_distribution, deliveredByChapter]);
  const pg = usePagination(rowsAll, 10);

  const flagged = questions.filter(q => (q.flags ?? []).length > 0).length;
  const lowQuality = questions.filter(q => (q.quality_score ?? 100) < 50).length;
  const targetTotal = blueprint.chapter_distribution.reduce((s, e) => s + e.count, 0);
  const delivered = questions.length;

  const effectiveRetryForm = formLabels?.includes(retryForm)
    ? retryForm
    : (formLabels?.[0] ?? retryForm);

  return (
    <div>
      <button type="button"
        aria-expanded={open}
        aria-controls="cov-panel"
        onClick={() => setOpen(v => !v)}
        className="flex min-h-[52px] w-full items-center justify-between gap-3 border-0 border-t border-hairline bg-transparent px-1 text-left font-display text-[15px] font-bold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
        <span>Coverage report <span className="font-sans text-[13px] font-normal text-muted">· questions per chapter vs target</span></span>
        <ChevronDown size={18} strokeWidth={1.8} aria-hidden className={`flex-none transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div id="cov-panel">
          <p className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className={delivered !== targetTotal ? 'font-semibold text-warning-ink' : 'font-semibold text-ink'}>
              <Term k="coverage" plain>{delivered} of {targetTotal} delivered</Term>
            </span>
            <span aria-hidden className="text-muted">·</span>
            {requestedTotal != null && requestedTotal !== targetTotal && (
              <>
                <span className="text-muted">Plan covers {targetTotal} of the {requestedTotal} requested</span>
                <span aria-hidden className="text-muted">·</span>
              </>
            )}
            <span className="text-muted">{flagged} flagged</span>
            <span aria-hidden className="text-muted">·</span>
            <span className="text-muted">{lowQuality} low quality</span>
          </p>

          {formLabels && formLabels.length > 1 && (
            <span className="mb-1.5 flex items-center gap-2 text-xs text-muted">
              <span>Retry form</span>
              <Segmented<string>
                label="Retry form"
                options={formLabels.map(f => ({ key: f, label: f }))}
                value={effectiveRetryForm}
                onChange={setRetryForm} />
            </span>
          )}

          {pg.pageItems.map(e => {
            const name = chapterNames?.[e.chapter_id] ?? `Chapter ${e.chapter_id}`;
            const noSource = noSourceIds.has(e.chapter_id);
            const low = lowByChapter.get(e.chapter_id) ?? 0;
            const short = e.got < e.count;
            const pct = e.count > 0 ? Math.min(100, Math.round((e.got / e.count) * 100)) : 100;
            const status = noSource && short
              ? { tone: 'danger' as const, label: 'No textbook content linked' }
              : short
                ? { tone: 'warning' as const, label: `Short by ${e.count - e.got}` }
                : low > 0
                  ? { tone: 'warning' as const, label: `${low} low quality` }
                  : { tone: 'success' as const, label: 'Complete' };
            return (
              <div key={e.chapter_id}
                className="grid grid-cols-[minmax(0,1fr)_70px_auto] items-center gap-3.5 border-b border-hairline py-[9px] text-[13.5px] last:border-b-0 md:grid-cols-[minmax(0,1fr)_120px_70px_auto]">
                {onFilterChapter ? (
                  <button type="button" aria-label={`${name}: ${e.got}/${e.count}`} title={`${name}: ${e.got}/${e.count}`}
                    onClick={() => onFilterChapter(e.chapter_id)}
                    className="min-w-0 truncate bg-transparent p-0 text-left font-medium text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    {name}
                  </button>
                ) : <span className="min-w-0 truncate font-medium text-ink">{name}</span>}
                <Track className="hidden md:block" value={pct} label={`${name} coverage`} tone={short ? 'accent' : 'slate'} />
                <span className="font-mono text-ink">{e.got} / {e.count}</span>
                <span className="flex flex-wrap items-center justify-end gap-2 text-right">
                  <Badge tone={status.tone}>{status.label}</Badge>
                  {short && !noSource && onRetryChapter && (
                    <button type="button" className={MINI} onClick={() => onRetryChapter(effectiveRetryForm, e.chapter_id)}>
                      {e.got > 0 ? 'Fill gap' : 'Retry'}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
          <PagedFooter className="mt-3" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Coverage pagination" />
        </div>
      )}
    </div>
  );
}
